import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core";
import { GridApi } from "ag-grid-community";
import classnames from "classnames";
import { SHIPMENT_REVIEW } from "constants/integration/shipment-review";
import type { IGridEntry, IOrderCurrency } from "types/cart/shipment-review";
import { format, formatCurrency } from "lib/globalize-util";
import { getFilteredTotals, getTotals } from "containers/legacy-cart/helpers/sizingViewHelper";

export interface IProps {
    entries: IGridEntry[];
    gridApi?: GridApi;
    currency?: IOrderCurrency;
}

export const GridViewCartTotals: FC<IProps> = ({ entries, gridApi, currency }) => {
    const [filteredTotals, setFilteredTotals] = useState<any>();
    const totals = useMemo(() => getTotals(entries), [entries]);
    const updateFilteredTotals = useCallback(
        ({ api }: { api: GridApi }) =>
            api?.isAnyFilterPresent() ? setFilteredTotals(getFilteredTotals(api)) : setFilteredTotals(null),
        []
    );

    const withCurrency = useCallback(
        (x: number) => (currency?.wholesale ? formatCurrency(x ?? 0, currency.wholesale.code) : x),
        [currency?.wholesale]
    );

    useEffect(() => {
        gridApi?.addEventListener("filterChanged", updateFilteredTotals);
        return () => {
            gridApi?.removeEventListener("filterChanged", updateFilteredTotals);
        };
    }, [gridApi, updateFilteredTotals]);

    useEffect(() => {
        if (gridApi) {
            updateFilteredTotals({ api: gridApi });
        }
    }, [gridApi, updateFilteredTotals, entries]);

    const classes = useStyles();
    return (
        <div className={classes.totalWrap}>
            <div
                className={classnames(classes.totalRow, classes.filteredTotal, {
                    "filtered-totals-hidden": !filteredTotals,
                })}
                data-test={SHIPMENT_REVIEW.GRID_VIEW_FILTERED_TOTALS}
            >
                <div>{format("FILTERED")}:</div>
                <div>
                    {format("Products")}:<span>{filteredTotals?.products}</span>
                </div>
                <div>
                    {format("Units")}:<span>{filteredTotals?.units}</span>
                </div>
                <div>
                    {format("Total Amount")}:<span>{withCurrency(filteredTotals?.wholesale)}</span>
                </div>
            </div>
            <div
                className={classnames(classes.totalRow, classes.total, {
                    [classes.fullHeightTotalRow]: !filteredTotals,
                })}
                data-test={SHIPMENT_REVIEW.GRID_VIEW_TOTALS}
            >
                <div>{format("TOTAL")}:</div>
                <div>
                    {format("Products")}:<span>{totals.products}</span>
                </div>
                <div>
                    {format("Units")}:<span>{totals.units}</span>
                </div>
                <div>
                    {format("Total Amount")}:<span>{withCurrency(totals?.wholesale)}</span>
                </div>
            </div>
        </div>
    );
};

const useStyles = makeStyles({
    totalWrap: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        width: "100%",
        fontFamily: "Lato, sans-serif",
        "& span": {
            marginLeft: "8px",
        },
        "& .filtered-totals-hidden": {
            height: "0px",
        },
    },
    totalRow: {
        height: "25px",
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        fontSize: "10px",
        transition: "height 0.15s ease-in",
        "& div": {
            padding: "0px 15px",
        },
    },
    fullHeightTotalRow: {
        height: "50px",
    },
    total: {
        backgroundColor: "#e1e1e1",
        "& span": {
            fontWeight: "bold",
            fontSize: "12px",
        },
        "& div:last-child span": {
            fontSize: "14px",
        },
    },
    filteredTotal: {
        backgroundColor: "#f0f0f0",
    },
});
